import BestRecord from "@/models/BestRecord";
import { BestRecordsCriteria } from "@/common/types/BestRecordsCriteria";
import { Memo } from "@/common/types/Memo";
import { Level } from "@/common/types/Level";
import { Difficulty } from "@/common/types/Difficulty";
import { RADAR_ELEMENT_STRING, RADAR_ELEMENT_KEY, RADAR_ORDER, Radar } from "@/common/types/Radar";
import Chart from "@/models/Chart";

export default class MusicRowData {
  readonly bestRecord1: BestRecord;
  readonly bestRecordCriteria1: BestRecordsCriteria;
  readonly bestRecord2?: BestRecord;
  readonly bestRecordCriteria2?: BestRecordsCriteria;
  readonly memoObj: Memo | null;

  constructor(
    bestRecord1: BestRecord,
    bestRecordCriteria1: BestRecordsCriteria,
    memo: Memo | null,
    bestRecord2?: BestRecord,
    bestRecordCriteria2?: BestRecordsCriteria
  ) {
    this.bestRecord1 = bestRecord1;
    this.bestRecordCriteria1 = bestRecordCriteria1;
    this.memoObj = memo;
    this.bestRecord2 = bestRecord2;
    this.bestRecordCriteria2 = bestRecordCriteria2;
  }

  get scoreDiff(): number | null {
    return this.bestRecord2 !== undefined ? this.bestRecord1.score - this.bestRecord2.score : null;
  }

  get scoreDiffString(): string | null {
    return this.scoreDiff === null ? null : MusicRowData.addNumberSign(this.scoreDiff);
  }

  get missCountDiff(): number | null {
    if (this.bestRecord2 === undefined) {
      return null;
    }
    if (this.bestRecord1.missCount >= 65535 || this.bestRecord2.missCount >= 65535) {
      return null;
    }
    return this.bestRecord1.missCount - this.bestRecord2.missCount;
  }

  get missCountDiffString(): string | null {
    return this.missCountDiff === null ? null : MusicRowData.addNumberSign(this.missCountDiff);
  }

  private static addNumberSign(n: number): string {
    return n < 0 ? n.toString() : "+" + n.toString();
  }

  get title(): string {
    return this.bestRecord1.chart.music.title;
  }

  get genre(): string {
    return this.bestRecord1.chart.music.genre;
  }

  get artist(): string {
    return this.bestRecord1.chart.music.artist;
  }

  get memo(): string | null {
    return this.memoObj?.memo ?? null;
  }

  get level(): Level | null {
    return this.bestRecord1.chart.level;
  }

  get minBpm(): number | null {
    return this.bestRecord1.chart.minBpm;
  }

  get maxBpm(): number | null {
    return this.bestRecord1.chart.maxBpm;
  }

  get difficulty(): Difficulty | null {
    return this.bestRecord1.chart.difficulty;
  }

  get radar(): Radar | null {
    return this.bestRecord1.chart.radar;
  }

  get chart(): Chart {
    return this.bestRecord1.chart;
  }
}

export const TEXT_KEYS = {
  title: { value: "title", title: "タイトル" },
  genre: { value: "genre", title: "ジャンル" },
  artist: { value: "artist", title: "アーティスト" },
  memo: { value: "memo", title: "メモ" },
};

type SortKey = {
  value: string;
  title: string;
};

type SortGroup = {
  group: string;
  groupTitle: string;
  children: SortKey[];
};

export const SORT_KEYS: (SortKey | SortGroup)[] = [
  { value: "title", title: "タイトル" },
  { value: "level", title: "レベル" },
  {
    group: "bpm",
    groupTitle: "BPM",
    children: [
      { value: "maxBpm", title: "最高BPM" },
      { value: "minBpm", title: "最低BPM" },
    ],
  },
  { value: "difficulty", title: "難易度" },
  { value: "chart.notes", title: "ノーツ数" },
  {
    group: "radar",
    groupTitle: "ノーツレーダー",
    children: [
      ...RADAR_ORDER.map((e) => ({
        value: `radar.${RADAR_ELEMENT_KEY[e]}`,
        title: `ノーツレーダー${RADAR_ELEMENT_STRING[e]}`,
      })),
    ],
  },
  { value: "bestRecord1.clearType", title: "クリアタイプ" },
  { value: "bestRecord1.scoreRate", title: "スコアレート" },
  { value: "bestRecord1.missCount", title: "ミスカウント" },
  {
    group: "playerRadar",
    groupTitle: "プレーヤーノーツレーダー",
    children: [
      ...RADAR_ORDER.map((e) => ({
        value: `bestRecord1.radar.${RADAR_ELEMENT_KEY[e]}`,
        title: `プレーヤーノーツレーダー${RADAR_ELEMENT_STRING[e]}`,
      })),
    ],
  },
  { value: "bestRecord1.djPoint", title: "DJポイント" },
  { value: "bestRecord1.date", title: "最終プレー日時" },
  { value: "scoreDiff", title: "スコア差分" },
  { value: "missCountDiff", title: "ミスカウント差分" },
];
